import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { traceDashApi } from '../utils/traceDashboardApi';
import { formatStampShort } from '../utils/datetime';
import { BAND_STYLE, BandHelp, health, HelpPop, Tip, TrendDelta } from '../pages/risk/riskUi';
import { Modal } from '../pages/review/reviewUi';
import '../styles/DocReview.css';
import '../styles/Risk.css';
import '../styles/TraceDashboard.css';

/**
 * The Traceability Dashboard (v4.27.0) — the fourth view of a trace panel, laid out exactly
 * like the Risk home (mockup https://claude.ai/artifact/YZZkXuyndmApdZ1CHx36QM, board 1):
 * score · coverage by level · what would lower it · status tiles · one grid row per level-1 item.
 *
 * Everything is read from the dashboard cache, which the funnel's background build fills
 * (design/DESIGN_TRACE_DASHBOARD.md §2). The grid is filtered and paged ON THE SERVER
 * (Critical Note #38): the tiles and the search box only set query parameters.
 */

const PAGE = 25;
const JOB_POLL_MS = 900;

export const STATUS_ORDER = ['verified', 'covered', 'partly', 'not_covered', 'notrun', 'failed', 'stale'];
export const STATUS_DOT = {
  verified: '#17603A', covered: '#7FB89E', partly: '#D49B2A', not_covered: '#7A1F05',
  notrun: '#A8A1BE', failed: '#C2410C', stale: '#3B6FB6',
};
export const STATUS_PILL = {
  verified: { background: '#DDF3E4', color: '#17603A' },
  covered: { background: '#EEF6F1', color: '#2E6B4E' },
  partly: { background: '#FFF1D6', color: '#7A4B00' },
  not_covered: { background: '#7A1F05', color: '#fff' },
  notrun: { background: '#F3F0F8', color: '#4A4560' },
  failed: { background: '#FBE0D5', color: '#8A2A0B' },
  stale: { background: '#E3ECF8', color: '#234B85' },
};

// What each part counts against the score. Mirrors trace_dashboard_engine._item_record /
// _score — change both together.
const DRIVER_HELP = {
  gaps: 'A level-1 item with no child at its first Must-have level counts fully; otherwise the share of its Must-have branches that stop short. An optional level never counts.',
  failed: 'Any test in the item\'s chain whose last run failed (Fail and Fail (Stop) both count).',
  notrun: 'The share of the item\'s tests that were never executed.',
  stale: 'The item was updated after its newest test run — its tests may no longer prove it.',
};
const DRIVER_MISSING = {
  failed: 'The panel has no test level.',
  notrun: 'The panel has no test level.',
  stale: 'The level-1 filter has no Updated date, or the tests carry no run date — add them to the filters to have this counted.',
};

const viewKey = (id) => `trace.dash.view.${id}`;
function readView(id) {
  try { return localStorage.getItem(viewKey(id)) === 'detail' ? 'detail' : 'summary'; } catch { return 'summary'; }
}

/** Build (or refresh) via the funnel's smart refresh and poll its job; `onDone` re-reads. */
export function useDashboardBuild(setupId, onDone) {
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const timer = useRef(null);
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);
  const build = async () => {
    setError('');
    try {
      const job = await traceDashApi.build(setupId);
      setProgress(0);
      setMessage(job.message || 'Building…');
      if (timer.current) clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const j = await traceDashApi.job(job.job_id);
          setProgress(j.progress || 0);
          setMessage(j.message || '');
          if (j.status === 'completed' || j.status === 'error') {
            clearInterval(timer.current);
            setProgress(null);
            setMessage('');
            if (j.status === 'error') setError(j.error || j.error_message || 'Build failed');
            onDone();
          }
        } catch {
          clearInterval(timer.current);
          setProgress(null);
        }
      }, JOB_POLL_MS);
    } catch (e) {
      setError(e.message);
    }
  };
  return { build, progress, message, error, busy: progress !== null };
}

export function TraceScoreBlock({ score, trend }) {
  if (!score || score.score === null || score.score === undefined) {
    return <div className="rsk-dim">Nothing to score yet.</div>;
  }
  const weights = score.drivers.filter((d) => d.available).map((d) => `${d.label.toLowerCase()} ${d.weight}`);
  return (
    <div className="rsk-score">
      <div className="rsk-lbl rsk-score-head">
        <span>Traceability score</span>
        <HelpPop label="How the traceability score works">
          <p className="rsk-help-lead">
            <strong>Higher is better.</strong> 100 means every level-1 item is fully traced and every test
            passed; 0 means nothing is traced or tested. Each part takes points off 100.
          </p>
          <BandHelp />
          <table className="rsk-help-tbl">
            <thead><tr><th>Part</th><th>What counts against you</th><th>Points lost</th></tr></thead>
            <tbody>
              {score.drivers.map((d) => (
                <tr key={d.key}>
                  <td>{d.label}</td>
                  <td>{DRIVER_HELP[d.key]}</td>
                  <td className="rsk-help-num">
                    {d.available ? <><strong>−{d.points}</strong> / {d.max}</> : <em>not counted</em>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="rsk-fine">
            Base weights are 45 · 30 · 15 · 10. A part the panel cannot answer is left out and the others are
            scaled up to fill 100. The score averages over every level-1 item in the panel.
          </p>
        </HelpPop>
      </div>
      <div className="rsk-score-main">
        <div className="rsk-score-num">{health(score.score)}</div>
        <div className="rsk-score-of">
          <div>of 100 · higher is better</div>
          <span className="rsk-pill" style={BAND_STYLE[score.band]}>{score.band}</span>
        </div>
        {trend?.length > 0 && (
          <div className="rsk-trend" aria-label="Score at each baseline">
            {trend.slice(-3).map((t) => (
              <div key={t.id}>{t.name} · <strong>{health(t.score) ?? '—'}</strong></div>
            ))}
            <div>
              Now · <strong>{health(score.score)}</strong>
              <TrendDelta from={trend[trend.length - 1]?.score} to={score.score} />
            </div>
          </div>
        )}
      </div>
      <div className="rsk-drivers">
        {score.drivers.map((d) => (
          <div className="rsk-drv tdb-drv" key={d.key}>
            <span>{d.label}</span>
            {d.available ? (
              <>
                <div className="rsk-trk" role="img" aria-label={`${d.label} ${d.points} of ${d.max} points lost`}>
                  <i style={{ width: `${d.max ? (d.points / d.max) * 100 : 0}%` }} />
                </div>
                <span className="rsk-drv-pts"><strong>−{d.points}</strong> / {d.max}</span>
              </>
            ) : (
              <Tip className="rsk-drv-na" heading="Left out of the score" tip={DRIVER_MISSING[d.key] || 'No data for this part.'}>
                not in panel
              </Tip>
            )}
          </div>
        ))}
      </div>
      <div className="rsk-fine">
        Weights: {weights.join(' · ')} (re-weighted to 100 over the parts the panel can answer). A gap is an
        unmet <strong>Must have</strong> level only — an optional level never counts against you.
      </div>
    </div>
  );
}

const pct = (n, t) => (t ? Math.round((100 * n) / t) : 0);

export function LevelCoverage({ levels, compact = false }) {
  if (!levels?.length) return <div className="rsk-dim">One level only — nothing to cover.</div>;
  return (
    <div className={`tdb-levels${compact ? ' tdb-levels--compact' : ''}`}>
      {levels.filter((l) => !l.passed).map((l) => {
        const p = pct(l.n, l.total);
        const cls = l.passed ? 'tdb-fill--pass' : l.is_run ? 'tdb-fill--run' : '';
        return (
          <div className="tdb-lvl" key={l.name}>
            <span className="tdb-lvl-name">
              <strong>{l.name}</strong>
              {!compact && (
                <span>{l.passed ? 'last run passed' : l.is_run ? 'executed' : l.must_have ? 'Must have' : 'Optional'}</span>
              )}
            </span>
            <div className="rsk-trk tdb-trk" role="img" aria-label={`${l.name} ${p} percent`}>
              <i className={cls} style={{ width: `${p}%` }} />
            </div>
            <span className="tdb-lvl-num"><strong>{p}%</strong>{!compact && <> · {l.n} / {l.total}</>}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The panel's tests by last result — distinct tests, each at its worst result. Replaces the old
 * "Passed" level bar, which counted only requirements whose EVERY test passed and so read 0% on
 * real panels where most tests pass (that count is kept as the line underneath).
 */
export function TestResults({ tests, n, compact = false }) {
  if (!tests || !tests.total) return null;
  const segs = [['pass', 'pass', 'rsk-seg--pass'], ['fail', 'fail', 'rsk-seg--fail'], ['notrun', 'not run', 'rsk-seg--no_run']];
  return (
    <div className={`tdb-tests${compact ? ' tdb-tests--compact' : ''}`}>
      <div className="tdb-lvl">
        <span className="tdb-lvl-name">
          <strong>Test results</strong>
          {!compact && <span>{tests.total} test{tests.total === 1 ? '' : 's'}, last run</span>}
        </span>
        <div className="rsk-bar tdb-tests-bar" role="img"
          aria-label={`${tests.pass} passed, ${tests.fail} failed, ${tests.notrun} not run`}>
          {segs.filter(([k]) => tests[k]).map(([k, , cls]) => (
            <i key={k} className={cls} style={{ width: `${(100 * tests[k]) / tests.total}%` }} />
          ))}
        </div>
        <span className="tdb-lvl-num"><strong>{pct(tests.pass, tests.total)}%</strong>{!compact && ' pass'}</span>
      </div>
      <div className="tdb-tests-legend">
        {segs.map(([k, label, cls]) => (
          <span key={k}><i className={cls} />{tests[k]} {label}</span>
        ))}
      </div>
      {!compact && (
        <div className="rsk-fine">
          {tests.all_passed} of {n} level-1 items have every test passing ({tests.with_tests} have tests).
        </div>
      )}
    </div>
  );
}

/** The grid's per-level coverage bar for one level-1 item. */
function CovCell({ bar, name }) {
  if (!bar || !bar.total) return <span className="rsk-dim">—</span>;
  const p = pct(bar.got, bar.total);
  const tone = p === 100 ? 'tdb-fill--pass' : p === 0 ? 'tdb-fill--gap' : 'tdb-fill--part';
  return (
    <Tip className="rsk-bar-cell" heading={`${name}: ${p}%`}
      tip={`${bar.got} of ${bar.total} branch${bar.total === 1 ? '' : 'es'} reach ${name}.`}>
      <span className="rsk-bar"><i className={tone} style={{ width: `${Math.max(p, p ? 4 : 0)}%` }} /></span>
      <span className="rsk-bar-num">{p}%</span>
    </Tip>
  );
}

function TestsCell({ tests }) {
  const total = (tests?.pass || 0) + (tests?.fail || 0) + (tests?.notrun || 0);
  if (!total) return <span className="rsk-dim">no tests</span>;
  const parts = [['pass', 'Pass', 'rsk-seg--pass'], ['fail', 'Fail', 'rsk-seg--fail'], ['notrun', 'Not run', 'rsk-seg--no_run']]
    .filter(([k]) => tests[k] > 0);
  return (
    <Tip className="rsk-bar-cell" heading={`${total} test${total === 1 ? '' : 's'}`}
      tip={parts.map(([k, l]) => `${l}: ${tests[k]}`).join(' · ')}>
      <span className="rsk-bar">
        {parts.map(([k, , cls]) => <i key={k} className={cls} style={{ width: `${(100 * tests[k]) / total}%` }} />)}
      </span>
      <span className="rsk-bar-num">{tests.pass}/{total}</span>
    </Tip>
  );
}

export function StatusPill({ status, label }) {
  return <span className="rsk-pill" style={STATUS_PILL[status]}>{label || status}</span>;
}

export default function TraceDashboard({ setupId, canManagePanels, aiStudio, onOpenInMatrix }) {
  const navigate = useNavigate();
  const [sum, setSum] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [view, setView] = useState(() => readView(setupId));
  const [taking, setTaking] = useState(false);
  const token = useRef(0);

  const load = useCallback(async () => {
    try {
      setError('');
      setSum(await traceDashApi.summary(setupId));
    } catch (e) {
      setError(e.message);
      setSum((x) => x || { cached: false });
    }
  }, [setupId]);
  useEffect(() => { load(); }, [load]);

  const { build, progress, message, error: buildError, busy } = useDashboardBuild(setupId, load);

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!sum?.cached) return;
    const mine = ++token.current;
    const params = { page, page_size: PAGE, status, q: query };
    if (view === 'detail') params.detail = 1;
    traceDashApi.rows(setupId, params)
      .then((d) => { if (mine === token.current) setRows(d); })
      .catch((e) => { if (mine === token.current) setError(e.message); });
  }, [setupId, sum, page, status, query, view]);

  const pickView = (v) => {
    setView(v);
    try { localStorage.setItem(viewKey(setupId), v); } catch { /* per-viewer convenience only */ }
  };
  const pickStatus = (st) => { setStatus((cur) => (cur === st ? '' : st)); setPage(1); };

  const s = sum?.summary;
  const openStudio = async () => {
    // Pre-select the level-1 items that still need a trace (design §3).
    let keys = [];
    try {
      const d = await traceDashApi.rows(setupId, { status: 'not_covered,partly', page_size: 200 });
      keys = (d.rows || []).map((r) => r.key).filter(Boolean);
    } catch { /* open it unselected */ }
    navigate(`/trace/build/${setupId}`, { state: { from: `/trace/matrix/${setupId}`, preselect: keys } });
  };

  const head = (
    <div className="rsk-card-head">
      <div style={{ minWidth: 0 }}>
        <h2 className="rsk-card-title">Requirement coverage and verification</h2>
        <div className="rsk-card-meta">
          {s ? <>Level 1: {s.base_name} · {s.n} items · {(s.grid_levels || []).length + 1} levels</> : 'Not built yet'}
          {sum?.built_at ? <> · built {formatStampShort(sum.built_at)}</> : null}
        </div>
      </div>
      <div className="rsk-actions">
        {aiStudio && (
          <button type="button" className="drv-btn drv-btn--primary" onClick={openStudio}
            title="Open the AI Trace Builder with the uncovered items selected">Studio — build with AI</button>
        )}
        <button type="button" className="drv-btn" onClick={() => navigate(`/trace/baselines/${setupId}`)}>
          Baselines{sum?.trend?.length ? ` · ${sum.trend.length}` : ''}
        </button>
        {canManagePanels && (
          <button type="button" className="drv-btn" onClick={() => setTaking(true)} disabled={!sum?.cached}>Take baseline</button>
        )}
        <button type="button" className="rsk-iconbtn" onClick={build} disabled={busy}
          title={busy ? 'Building…' : 'Refresh from Orcanos (re-reads only what changed)'} aria-label="Refresh from Orcanos">
          <span className={busy ? 'rsk-spin' : undefined}>⟳</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="tdb-wrap">
      <section className="rsk-card" aria-label="Traceability dashboard">
        {head}
        {busy && (
          <div className="tdb-progress" role="status">
            <div className="rsk-trk"><i style={{ width: `${progress || 0}%` }} /></div>
            <span>{message || 'Building…'}</span>
          </div>
        )}
        {(error || buildError) && <div className="drv-error" role="alert" style={{ margin: '12px 18px 0' }}>{error || buildError}</div>}
        {!sum && !error && <div className="drv-muted" style={{ padding: 18 }}>Loading…</div>}

        {sum && !sum.cached && !busy && (
          <div className="tdb-empty">
            <p>The dashboard is built from the whole matrix — every page, every level — in the background.</p>
            <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={build}>Build the dashboard</button>
          </div>
        )}

        {s && (
          <>
            <div className="rsk-grid3">
              <div className="rsk-cell"><TraceScoreBlock score={s.score} trend={sum.trend} /></div>
              <div className="rsk-cell">
                <div className="rsk-lbl">Coverage by level</div>
                <LevelCoverage levels={s.levels} />
                <div className="rsk-fine">Share of the {s.n} level-1 items whose chain reaches each level.</div>
                <TestResults tests={s.tests} n={s.n} />
                {s.has_tests && !s.tests && (
                  <div className="rsk-fine">⟳ Refresh to see the test results here.</div>
                )}
              </div>
              <div className="rsk-cell">
                <div className="rsk-lbl rsk-score-head">
                  <span>What would raise the score most</span>
                  <HelpPop label="How this list works" end>
                    <p className="rsk-help-lead">
                      <strong>The five level-1 items that take the most off the score.</strong> Fix one and the
                      score rises by its number.
                    </p>
                    <p className="rsk-fine">
                      An item&apos;s points are its share of the same four parts. The line under its name is the
                      biggest of them. Items with the same problem score the same.
                    </p>
                  </HelpPop>
                </div>
                {(s.score?.lower || []).length === 0 && <div className="rsk-dim">Nothing — every item is fully traced and verified.</div>}
                {(s.score?.lower || []).map((l) => (
                  <button type="button" key={l.id} className="rsk-lower" onClick={() => onOpenInMatrix?.(l.key)}
                    title="Show it in the matrix">
                    <span className="rsk-lower-key">{l.key}</span>
                    <span className="rsk-lower-name">{l.name}<span>{l.why}</span></span>
                    <span className="rsk-lower-pts">+{l.points}</span>
                  </button>
                ))}
                {aiStudio && (s.status?.not_covered || s.status?.partly) ? (
                  <button type="button" className="drv-link tdb-studio-link" onClick={openStudio}>
                    Fix the uncovered ones in Studio →
                  </button>
                ) : null}
              </div>
            </div>

            <div className="rsk-band">
              <div className="rsk-lbl">
                Verification status — {s.base_name} → {(s.grid_levels || []).map((g) => g.name).join(' → ')}
                {!s.has_tests && <span className="rsk-lbl-note"> · the panel has no test level, so only coverage is scored</span>}
              </div>
              <div className="rsk-tiles">
                {STATUS_ORDER.filter((k) => (s.status?.[k] || 0) > 0 || ['verified', 'not_covered', 'failed', 'notrun'].includes(k))
                  .filter((k) => s.has_tests || !['verified', 'failed', 'notrun', 'stale'].includes(k))
                  .map((k) => {
                    const on = status === k;
                    return (
                      <button type="button" key={k} className={`rsk-tile${on ? ' rsk-tile--on' : ''}`}
                        onClick={() => pickStatus(k)} aria-pressed={on}>
                        <span className="rsk-tile-name"><i style={{ background: STATUS_DOT[k] }} />{s.status_labels?.[k] || k}</span>
                        <span className="rsk-tile-n">{s.status?.[k] || 0}</span>
                        <span className="rsk-tile-hint">{s.status_hints?.[k]}</span>
                      </button>
                    );
                  })}
              </div>
            </div>

            <div className="rsk-reg-bar">
              <div className="rsk-lbl">
                {s.base_name || 'Level 1'}
                {status && (
                  <button type="button" className="drv-link rsk-small-link" onClick={() => pickStatus(status)}>
                    clear filter ({s.status_labels?.[status]})
                  </button>
                )}
              </div>
              <div className="rsk-reg-tools">
                <div className="rsk-seg" role="group" aria-label="Grid view">
                  <button type="button" className={view === 'summary' ? 'on' : ''} aria-pressed={view === 'summary'}
                    onClick={() => pickView('summary')}>Summary</button>
                  <button type="button" className={view === 'detail' ? 'on' : ''} aria-pressed={view === 'detail'}
                    onClick={() => pickView('detail')}>Detail</button>
                </div>
                <input type="search" className="drv-input rsk-search" aria-label="Search level-1 items"
                  placeholder="Search key or name" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>

            {view === 'detail' ? (
              <DetailGrid rows={rows} onOpen={onOpenInMatrix} />
            ) : (
              <div className="rsk-table-wrap rsk-table-wrap--reg">
                <table className="rsk-table">
                  <thead>
                    <tr>
                      <th>Key / {s.base_name || 'Item'}</th>
                      {(s.grid_levels || []).map((g) => (
                        <th key={g.name}>
                          {g.name}
                          {g.must_have && !g.is_run && <Tip className="tdb-must" heading="Must have" tip="A missing child here is a gap.">●</Tip>}
                        </th>
                      ))}
                      {s.has_tests && <th>Verification</th>}
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rows?.rows || []).map((r) => (
                      <tr key={r.id} className="rsk-tr" onClick={() => onOpenInMatrix?.(r.key)}>
                        <td>
                          {r.url
                            ? <Tip as="a" className="rsk-key rsk-key--link" href={r.url} target="_blank" rel="noopener noreferrer"
                                tip="Open in Orcanos (new tab)" onClick={(e) => e.stopPropagation()}>{r.key} ↗</Tip>
                            : <span className="rsk-key">{r.key}</span>}
                          <div className="rsk-name">{r.name}</div>
                        </td>
                        {(s.grid_levels || []).map((g, i) => <td key={g.name}><CovCell bar={r.bars?.[i]} name={g.name} /></td>)}
                        {s.has_tests && <td><TestsCell tests={r.tests} /></td>}
                        <td><StatusPill status={r.status} label={s.status_labels?.[r.status]} /></td>
                        <td className="rsk-sub rsk-nowrap">{r.updated || '—'}</td>
                      </tr>
                    ))}
                    {rows && rows.rows.length === 0 && (
                      <tr><td colSpan={(s.grid_levels || []).length + 4} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No items match.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="rsk-pager">
              <span>{rows?.total ? `${(rows.page - 1) * PAGE + 1}–${Math.min(rows.page * PAGE, rows.total)} of ${rows.total}` : '0 items'}</span>
              <button type="button" className="drv-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <button type="button" className="drv-btn" disabled={!rows || page >= rows.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              <span className="tdb-legend"><span className="tdb-must">●</span> Must-have level · bars: share of the item&apos;s branches that reach the level</span>
            </div>
          </>
        )}
      </section>
      {taking && (
        <TraceBaselineModal setupId={setupId} builtAt={sum?.built_at} onClose={() => setTaking(false)}
          onTaken={() => { setTaking(false); load(); }} />
      )}
    </div>
  );
}

function DetailGrid({ rows, onOpen }) {
  const cols = (rows?.columns || []).filter((c) => c !== 'Key');
  return (
    <div className="rsk-table-wrap rsk-table-wrap--reg">
      <table className="rsk-table rsk-table--detail">
        <thead>
          <tr><th className="rsk-sticky">Key</th>{cols.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {(rows?.rows || []).map((r) => (
            <tr key={r.id} className="rsk-tr" onClick={() => onOpen?.(r.key)}>
              <td className="rsk-sticky">
                {r.url
                  ? <a className="rsk-key rsk-key--link" href={r.url} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}>{r.key} ↗</a>
                  : <span className="rsk-key">{r.key}</span>}
              </td>
              {cols.map((c) => {
                const v = String(r.cells?.[c] ?? '');
                return <td key={c}><div className="rsk-dcell" title={v.length > 80 ? v : undefined}>{v || '—'}</div></td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TraceBaselineModal({ setupId, builtAt, onClose, onTaken }) {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!name.trim()) { setError('Give the baseline a name, e.g. “Design freeze 2”.'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await traceDashApi.takeBaseline(setupId, { name: name.trim(), note: note.trim() });
      onTaken(r.baseline);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Take baseline"
      subtitle={`A read-only snapshot of the latest build${builtAt ? ` (${formatStampShort(builtAt)})` : ''}: every level-1 item, its coverage per level, its test results and the score. Refresh first if you need the very latest from Orcanos.`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Take baseline'}
          </button>
        </>
      )}
    >
      <label className="drv-field">
        <span className="drv-lbl">Name</span>
        <input className="drv-input" value={name} onChange={(e) => { setName(e.target.value); setError(''); }}
          placeholder="e.g. Design freeze 2 · V&V start" maxLength={120} autoFocus />
      </label>
      <label className="drv-field">
        <span className="drv-lbl">Note <span>— optional</span></span>
        <textarea className="drv-input rsk-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Design review DR-3, before the verification campaign" maxLength={1000} />
      </label>
      <p className="drv-small" style={{ margin: 0 }}>
        Baselines can be archived but never edited or deleted. Each one carries a SHA-256 of its content.
      </p>
      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}

/**
 * The Dashboard view of a panel card on the Traceability dashboard (mockup board 2): score,
 * level bars, four tiles, the single top fix. Reads only the cache — a dashboard of cards must
 * never fire one build per card; the build is the card's own ↻ button.
 */
export function TraceDashboardMini({ dash, loading, error, progress, message, onBuild }) {
  if (progress !== null && progress !== undefined) {
    return (
      <div className="tdb-mini-msg" role="status">
        <div className="rsk-trk" style={{ width: 220 }}><i style={{ width: `${progress}%` }} /></div>
        <span>{message || 'Building…'}</span>
      </div>
    );
  }
  if (loading && !dash) return <div className="preview-loading">Loading dashboard…</div>;
  if (error) return <div className="preview-error">{error}</div>;
  if (!dash || !dash.cached || !dash.summary) {
    return (
      <div className="tdb-mini-msg">
        <span>Not built yet — the dashboard is computed from the whole matrix in the background.</span>
        <button type="button" className="btn-action" onClick={onBuild}>↻ Build</button>
      </div>
    );
  }
  const s = dash.summary;
  const sc = s.score || {};
  const trend = dash.trend || [];
  const prev = trend.length ? trend[trend.length - 1] : null;
  const top = (sc.lower || [])[0];
  const tiles = (s.has_tests ? ['verified', 'not_covered', 'failed', 'notrun'] : ['covered', 'partly', 'not_covered'])
    .map((k) => ({ k, n: s.status?.[k] || 0, label: s.status_labels?.[k] || k }));
  return (
    <div className="tdb-mini">
      <div className="tdb-mini-top">
        <div className="tdb-mini-score">
          <div className="rsk-lbl">Traceability score</div>
          <div className="tdb-mini-num">
            <strong>{health(sc.score) ?? '—'}</strong>
            <span>
              of 100
              {prev && prev.score !== null && prev.score !== sc.score && (
                <><br /><TrendDelta from={prev.score} to={sc.score} /> since {prev.name}</>
              )}
            </span>
          </div>
          {sc.band && <span className="rsk-pill" style={BAND_STYLE[sc.band]}>{sc.band}</span>}
        </div>
        <div className="tdb-mini-cov">
          <LevelCoverage levels={s.levels} compact />
          <TestResults tests={s.tests} n={s.n} compact />
        </div>
      </div>
      <div className="tdb-mini-tiles">
        {tiles.map((t) => (
          <div className="tdb-mt" key={t.k}>
            <span className="tdb-mt-name"><i style={{ background: STATUS_DOT[t.k] }} />{t.label}</span>
            <span className="tdb-mt-n">{t.n}</span>
          </div>
        ))}
      </div>
      {top && (
        <div className="tdb-mini-fix">
          <span className="rsk-lbl">Top fix</span>
          <span className="tdb-mini-fix-key">{top.key}</span>
          <span className="tdb-mini-fix-why" title={`${top.name} — ${top.why}`}>{top.name} — {top.why}</span>
          <strong>+{top.points}</strong>
        </div>
      )}
      <div className="rsk-fine">Built {formatStampShort(dash.built_at)} · {s.n} items</div>
    </div>
  );
}
